import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "@jsq/ds/core";
import { ComplianceCaseTypeEnum as CaseType } from "~/graphql/supergraphEnums";
import {
  useCreateEscalationMutation,
  useDeleteEscalationMutation,
  useUpdateEscalationMutation,
  useGetPicrPositionDetailsQuery,
} from "~/graphql/supergraphHooks";
import {
  type ComplianceCaseTypeEnum,
  type GetComplianceCaseQuery,
} from "~/graphql/supergraphTypes";
import { extractChildProfiles } from "../../Documents/utils";
import { getProfileName } from "../../Profiles/utils";

export type EscalationRecord = NonNullable<
  NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"]
>[0];

type ProfileOption = { label: string; value: number };

type Args = {
  caseId: number;
  caseType: ComplianceCaseTypeEnum | undefined;
  profileId: number | undefined;
  profilesForCase?: GetComplianceCaseQuery["complianceCase"] | null | undefined;
  mode: "create" | "edit";
  escalationToEdit?: EscalationRecord;
  onDone: () => void;
};

const toastError = (e: unknown) =>
  toast.error(e instanceof Error ? e.message : "An unknown error occurred");

/** Create/edit escalation form state and mutations, shared by the modal and the full-screen takeover. */
export const useEscalationForm = ({
  caseId,
  caseType,
  profileId,
  profilesForCase,
  mode,
  escalationToEdit,
  onDone,
}: Args) => {
  const [issueInput, setIssueInput] = useState<string>(
    typeof escalationToEdit?.description === "string" ? escalationToEdit.description : "",
  );
  const [errorVisible, setErrorVisible] = useState(false);
  const isPicrCase = caseType === CaseType.PaymentInstructionChangeReview;
  const { data: picrPositionData } = useGetPicrPositionDetailsQuery({
    variables: { caseId },
    skip: !isPicrCase,
  });

  const profileOptions = useMemo(() => {
    if (!profilesForCase) return [] as ProfileOption[];
    const seen = new Set<number>();
    const baseOptions: ProfileOption[] = [];
    const createdOptions: ProfileOption[] = [];
    const linkedInvestingOptions: ProfileOption[] = [];
    const linkedCaseProfileOptions: ProfileOption[] = [];

    if (isPicrCase && picrPositionData?.picrPositionDetailsByCaseId) {
      for (const pos of picrPositionData.picrPositionDetailsByCaseId) {
        const loCase = pos as {
          linkedOnboardingCase?: {
            investingProfile?: { id?: number } | null;
            profiles?: Array<{ id?: number } | null> | null;
          } | null;
        };

        const linkedInvesting = loCase.linkedOnboardingCase?.investingProfile as
          Parameters<typeof getProfileName>[0] | undefined;
        const linkedId = (linkedInvesting as { id?: number } | undefined)?.id;
        if (linkedInvesting && typeof linkedId === "number" && !seen.has(linkedId)) {
          linkedInvestingOptions.push({
            label: getProfileName(linkedInvesting),
            value: linkedId,
          });
          seen.add(linkedId);
        }

        const created = (pos as { createdProfile?: { id?: number } | null }).createdProfile as
          Parameters<typeof getProfileName>[0] | undefined;
        const createdId = (created as { id?: number } | undefined)?.id;
        if (created && typeof createdId === "number" && !seen.has(createdId)) {
          seen.add(createdId);
          createdOptions.push({ label: getProfileName(created), value: createdId });
        }

        const linkedProfiles = loCase.linkedOnboardingCase?.profiles || [];
        for (const linkedProfile of linkedProfiles) {
          if (
            linkedProfile &&
            typeof linkedProfile.id === "number" &&
            !seen.has(linkedProfile.id)
          ) {
            seen.add(linkedProfile.id);
            linkedCaseProfileOptions.push({
              label: getProfileName(linkedProfile as Parameters<typeof getProfileName>[0]),
              value: linkedProfile.id,
            });
          }
        }
      }
    }

    for (const p of extractChildProfiles(profilesForCase) ?? []) {
      if (!p) continue;
      const id = (p as { id?: number }).id;
      if (typeof id === "number" && !seen.has(id)) {
        seen.add(id);
        baseOptions.push({
          label: getProfileName(p),
          value: id,
        });
      }
    }

    return [
      ...linkedInvestingOptions,
      ...createdOptions,
      ...linkedCaseProfileOptions,
      ...baseOptions,
    ];
  }, [profilesForCase, isPicrCase, picrPositionData]);

  const initialSelectedProfileId = useMemo(() => {
    if (mode === "edit") {
      const pid = (escalationToEdit as unknown as { profileId?: number | null })?.profileId;
      if (typeof pid === "number") return pid;
    }
    if (isPicrCase && profileOptions.length > 0) return profileOptions[0].value;
    return profileId ?? profilesForCase?.investingProfile?.id;
  }, [
    mode,
    escalationToEdit,
    isPicrCase,
    profileOptions,
    profilesForCase?.investingProfile?.id,
    profileId,
  ]);

  const [selectedProfileId, setSelectedProfileId] = useState<number | undefined>(
    initialSelectedProfileId,
  );

  useEffect(() => {
    if (mode === "edit") {
      const pid = (escalationToEdit as unknown as { profileId?: number | null })?.profileId;
      if (typeof pid === "number") setSelectedProfileId(pid);
    }
  }, [mode, escalationToEdit]);

  const usesSelectedProfile = !!profilesForCase && caseType !== CaseType.RecurringHit;
  const effectiveProfileId = usesSelectedProfile ? selectedProfileId : profileId;

  const selectedProfileName = useMemo(() => {
    if (!profilesForCase || effectiveProfileId == null) return "";
    if (effectiveProfileId === profilesForCase.investingProfile?.id) {
      const base = profilesForCase.investingProfile;
      return base ? getProfileName(base) : "";
    }
    return profileOptions.find((o) => o.value === effectiveProfileId)?.label ?? "";
  }, [profilesForCase, profileOptions, effectiveProfileId]);

  const hasDuplicateEscalationForSelectedProfile = useMemo(() => {
    if (!profilesForCase || effectiveProfileId == null) return false;
    return (profilesForCase.escalations ?? []).some((e) => {
      const isSelf =
        mode === "edit" && Boolean(escalationToEdit) && e?.id === escalationToEdit?.id;
      return e?.profileId === effectiveProfileId && !isSelf;
    });
  }, [profilesForCase, effectiveProfileId, mode, escalationToEdit]);

  const [createEscalation, { loading: createLoading }] = useCreateEscalationMutation({
    refetchQueries: ["GetComplianceCase"],
    onError: toastError,
    onCompleted: () => {
      toast.success("Escalation created successfully");
      onDone();
    },
  });

  const [deleteEscalation, { loading: deleteLoading }] = useDeleteEscalationMutation({
    refetchQueries: ["GetComplianceCase"],
    onError: toastError,
    onCompleted: () => {
      toast.success("Escalation deleted successfully");
      onDone();
    },
  });

  const [updateEscalation, { loading: updateLoading }] = useUpdateEscalationMutation({
    refetchQueries: ["GetComplianceCase"],
    onError: toastError,
    onCompleted: () => {
      toast.success("Escalation updated successfully");
      onDone();
    },
  });

  const validateIssue = useCallback(() => {
    const trimmed = issueInput.trim();
    setIssueInput(trimmed);
    if (!trimmed) {
      setErrorVisible(true);
      return null;
    }
    return trimmed;
  }, [issueInput]);

  const submitCreate = useCallback(async () => {
    const description = validateIssue();
    if (description === null) return;
    await createEscalation({
      variables: {
        caseId,
        description,
        profileId: usesSelectedProfile ? selectedProfileId : undefined,
      },
    });
  }, [createEscalation, validateIssue, caseId, usesSelectedProfile, selectedProfileId]);

  const saveEdit = useCallback(async () => {
    if (!escalationToEdit) return;
    const description = validateIssue();
    if (description === null) return;
    await updateEscalation({
      variables: {
        id: escalationToEdit.id,
        profileId: usesSelectedProfile ? selectedProfileId : escalationToEdit.profileId,
        description,
      },
    });
  }, [
    escalationToEdit,
    validateIssue,
    usesSelectedProfile,
    selectedProfileId,
    updateEscalation,
  ]);

  const removeEscalation = useCallback(async () => {
    if (!escalationToEdit) return;
    await deleteEscalation({ variables: { id: escalationToEdit.id } });
  }, [deleteEscalation, escalationToEdit]);

  const setIssue = useCallback((value: string) => {
    setErrorVisible(false);
    setIssueInput(value);
  }, []);

  return {
    issueInput,
    setIssue,
    errorVisible,
    profileOptions,
    selectedProfileId,
    setSelectedProfileId,
    effectiveProfileId,
    selectedProfileName,
    hasDuplicateEscalationForSelectedProfile,
    submitCreate,
    saveEdit,
    removeEscalation,
    createLoading,
    updateLoading,
    deleteLoading,
  };
};
